import React, { useState, useEffect } from 'react';
import { useAuth } from '../../../contexts/AuthContext';
import { useToast } from '../../Toast';
import { useConfirm } from '../../../hooks/useConfirm';
import { FirebaseCustomMeasureService, CustomMeasure, MeasureBand, MeasureQuestion } from '../../../services/firebase/FirebaseCustomMeasureService';
import VoiceInputButton from '../../VoiceInputButton';

const BAND_COLORS = ['#38BDF8', '#10B981', '#f59e0b', '#f97316', '#EF4444'];

const DEFAULT_SCALE_LABELS = [
  { value: '0', label: 'Not at all' },
  { value: '1', label: 'Several days' },
  { value: '2', label: 'Over half the days' },
  { value: '3', label: 'Nearly every day' },
];

interface CustomMeasureBuilderProps {
  practiceOwnerId?: string;
}

const CustomMeasureBuilder: React.FC<CustomMeasureBuilderProps> = ({ practiceOwnerId: propOwnerId }) => {
  const { user } = useAuth();
  const toast = useToast();
  const { confirm, ConfirmDialog } = useConfirm();
  const [measures, setMeasures] = useState<CustomMeasure[]>([]);
  const [editing, setEditing] = useState<CustomMeasure | null>(null);
  const [showBuilder, setShowBuilder] = useState(false);

  // Builder form state
  const [title, setTitle] = useState('');
  const [description, setDescription] = useState('');
  const [citation, setCitation] = useState('');
  const [scaleMax, setScaleMax] = useState(3);
  const [scaleLabels, setScaleLabels] = useState(DEFAULT_SCALE_LABELS);
  const [questions, setQuestions] = useState<MeasureQuestion[]>([
    { id: 'q1', text: '' },
  ]);
  const [bands, setBands] = useState<MeasureBand[]>([
    { min: 0, max: 4, label: 'None-minimal', color: BAND_COLORS[0] },
    { min: 5, max: 9, label: 'Mild', color: BAND_COLORS[1] },
    { min: 10, max: 14, label: 'Moderate', color: BAND_COLORS[2] },
    { min: 15, max: 21, label: 'Severe', color: BAND_COLORS[4] },
  ]);

  const practiceId = propOwnerId || user?.uid || '';

  useEffect(() => {
    if (!practiceId) return;
    const unsub = FirebaseCustomMeasureService.subscribeToPracticeMeasures(practiceId, setMeasures);
    return unsub;
  }, [practiceId]);

  const actualMax = Math.max(...scaleLabels.map(sl => parseInt(sl.value, 10) || 0));
  const maxScore = questions.length * actualMax;

  const resetForm = () => {
    setTitle('');
    setDescription('');
    setCitation('');
    setScaleMax(3);
    setScaleLabels(DEFAULT_SCALE_LABELS);
    setQuestions([{ id: 'q1', text: '' }]);
    setBands([
      { min: 0, max: 4, label: 'None-minimal', color: BAND_COLORS[0] },
      { min: 5, max: 9, label: 'Mild', color: BAND_COLORS[1] },
      { min: 10, max: 14, label: 'Moderate', color: BAND_COLORS[2] },
      { min: 15, max: 21, label: 'Severe', color: BAND_COLORS[4] },
    ]);
    setEditing(null);
  };

  const openBuilder = (measure?: CustomMeasure) => {
    if (measure) {
      setEditing(measure);
      setTitle(measure.title);
      setDescription(measure.description || '');
      setCitation(measure.citation || '');
      setScaleMax(measure.scaleMax);
      setScaleLabels(measure.scaleLabels);
      setQuestions(measure.questions);
      setBands(measure.bands);
    } else {
      resetForm();
    }
    setShowBuilder(true);
  };

  const addQuestion = () => {
    const nextId = `q${questions.length + 1}`;
    setQuestions([...questions, { id: nextId, text: '' }]);
  };

  const removeQuestion = (index: number) => {
    if (questions.length <= 1) return;
    setQuestions(questions.filter((_, i) => i !== index));
  };

  const updateQuestion = (index: number, text: string) => {
    const updated = [...questions];
    updated[index] = { ...updated[index], text };
    setQuestions(updated);
  };

  const addBand = () => {
    const lastMax = bands.length > 0 ? bands[bands.length - 1].max + 1 : 0;
    setBands([...bands, {
      min: lastMax,
      max: lastMax + 5,
      label: '',
      color: BAND_COLORS[Math.min(bands.length, BAND_COLORS.length - 1)],
    }]);
  };

  const removeBand = (index: number) => {
    if (bands.length <= 1) return;
    setBands(bands.filter((_, i) => i !== index));
  };

  const updateBand = (index: number, field: keyof MeasureBand, value: any) => {
    const updated = [...bands];
    updated[index] = { ...updated[index], [field]: field === 'min' || field === 'max' ? Number(value) : value };
    setBands(updated);
  };

  const updateScaleLabel = (index: number, label: string) => {
    const updated = [...scaleLabels];
    updated[index] = { ...updated[index], label };
    setScaleLabels(updated);
  };

  const handleScaleMaxChange = (newMax: number) => {
    setScaleMax(newMax);
    const labels = [];
    for (let i = 0; i <= newMax; i++) {
      labels.push({
        value: String(i),
        label: scaleLabels[i]?.label || `Level ${i}`,
      });
    }
    setScaleLabels(labels);
  };

  const handleSave = async () => {
    if (!title.trim()) {
      toast.warning('Missing Title', 'Please enter a measure name.');
      return;
    }
    const emptyQ = questions.find(q => !q.text.trim());
    if (emptyQ) {
      toast.warning('Empty Question', 'All questions must have text.');
      return;
    }
    const emptyBand = bands.find(b => !b.label.trim());
    if (emptyBand) {
      toast.warning('Empty Band', 'All severity bands must have a label.');
      return;
    }

    try {
      const measureData = {
        id: editing?.id || '',
        title: title.trim(),
        description: description.trim() || undefined,
        citation: citation.trim() || undefined,
        maxScore,
        scaleMax,
        scaleLabels,
        questions: questions.map((q, i) => ({ id: `q${i + 1}`, text: q.text.trim() })),
        bands,
        createdBy: user?.uid || '',
        practiceId,
      };

      if (editing?.id) {
        await FirebaseCustomMeasureService.updateMeasure(editing.id, measureData);
        toast.success('Measure Updated', `"${title}" has been updated.`);
      } else {
        await FirebaseCustomMeasureService.saveMeasure(measureData as any);
        toast.success('Measure Created', `"${title}" is now available in the Measures tab.`);
      }
      setShowBuilder(false);
      resetForm();
    } catch (error) {
      console.error('Error saving measure:', error);
      toast.error('Save Failed', 'Could not save the measure. Please try again.');
    }
  };

  const handleDelete = async (measure: CustomMeasure) => {
    const confirmed = await confirm({
      title: 'Delete Measure',
      message: `Are you sure you want to delete "${measure.title}"? This cannot be undone. Historical scores will be preserved.`,
      confirmLabel: 'Delete',
      variant: 'danger',
    });
    if (!confirmed) return;
    try {
      await FirebaseCustomMeasureService.deleteMeasure(measure.id);
      toast.success('Deleted', `"${measure.title}" has been removed.`);
    } catch (error) {
      console.error('Error deleting measure:', error);
      toast.error('Delete Failed', 'Could not delete the measure.');
    }
  };

  const inputStyle: React.CSSProperties = {
    width: '100%',
    padding: '10px',
    backgroundColor: '#374151',
    color: '#f9fafb',
    border: '1px solid #4b5563',
    borderRadius: '6px',
    fontSize: '14px',
  };

  const labelStyle: React.CSSProperties = {
    display: 'block',
    marginBottom: '6px',
    fontSize: '13px',
    color: '#e5e7eb',
    fontWeight: '600',
  };

  // ─── Builder Form ───
  if (showBuilder) {
    return (
      <div>
        <div style={{ display: 'flex', alignItems: 'center', gap: '12px', marginBottom: '24px' }}>
          <button
            onClick={() => { setShowBuilder(false); resetForm(); }}
            style={{ background: 'none', border: 'none', color: '#6CA6C1', cursor: 'pointer', fontSize: '14px' }}
          >
            &larr; Back
          </button>
          <h1 style={{ fontSize: '24px', fontWeight: '600', color: '#F7E9A0', margin: 0 }}>
            {editing ? 'Edit Measure' : 'Create Custom Measure'}
          </h1>
        </div>

        {/* Name & Description */}
        <div style={{ marginBottom: '20px' }}>
          <label style={labelStyle}>Measure Name *</label>
          <input value={title} onChange={e => setTitle(e.target.value)} placeholder="e.g., ADHD Self-Report Scale" style={inputStyle} />
        </div>
        <div style={{ marginBottom: '20px' }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '6px' }}>
            <label style={{ ...labelStyle, marginBottom: 0 }}>Description</label>
            <VoiceInputButton onResult={text => setDescription(prev => prev + ' ' + text)} />
          </div>
          <textarea value={description} onChange={e => setDescription(e.target.value)} placeholder="Brief description of what this measure assesses..." rows={2} style={{ ...inputStyle, resize: 'vertical' }} />
        </div>

        {/* Response Scale */}
        <div style={{ backgroundColor: '#1e293b', border: '1px solid #4b5563', borderRadius: '8px', padding: '16px', marginBottom: '20px' }}>
          <h3 style={{ color: '#6CA6C1', margin: '0 0 12px 0', fontSize: '16px' }}>Response Scale</h3>
          <div style={{ marginBottom: '12px' }}>
            <label style={labelStyle}>Scale Range: 0 to</label>
            <select value={scaleMax} onChange={e => handleScaleMaxChange(Number(e.target.value))} style={{ ...inputStyle, width: '100px' }}>
              <option value={1}>1</option>
              <option value={2}>2</option>
              <option value={3}>3</option>
              <option value={4}>4</option>
              <option value={5}>5</option>
              <option value={10}>10</option>
            </select>
          </div>
          <label style={labelStyle}>Scale Labels</label>
          <div style={{ display: 'grid', gap: '8px' }}>
            {scaleLabels.map((sl, i) => (
              <div key={i} style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                <span style={{ color: '#9ca3af', fontSize: '13px', width: '24px', textAlign: 'center' }}>{sl.value}</span>
                <input value={sl.label} onChange={e => updateScaleLabel(i, e.target.value)} placeholder={`Label for ${sl.value}`} style={{ ...inputStyle, flex: 1 }} />
              </div>
            ))}
          </div>
        </div>

        {/* Questions */}
        <div style={{ backgroundColor: '#1e293b', border: '1px solid #4b5563', borderRadius: '8px', padding: '16px', marginBottom: '20px' }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '12px' }}>
            <h3 style={{ color: '#6CA6C1', margin: 0, fontSize: '16px' }}>Questions ({questions.length})</h3>
            <button onClick={addQuestion} style={{ padding: '6px 14px', backgroundColor: '#6CA6C1', color: '#fff', border: 'none', borderRadius: '6px', fontSize: '13px', cursor: 'pointer' }}>
              + Add Question
            </button>
          </div>
          <p style={{ color: '#9ca3af', fontSize: '12px', margin: '0 0 12px 0' }}>
            Max possible score: {maxScore} ({questions.length} questions x {scaleMax} max per question)
          </p>
          <div style={{ display: 'grid', gap: '8px' }}>
            {questions.map((q, i) => (
              <div key={q.id} style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                <span style={{ color: '#9ca3af', fontSize: '13px', width: '28px' }}>{i + 1}.</span>
                <input
                  value={q.text}
                  onChange={e => updateQuestion(i, e.target.value)}
                  placeholder={`Question ${i + 1} text...`}
                  style={{ ...inputStyle, flex: 1 }}
                />
                {questions.length > 1 && (
                  <button onClick={() => removeQuestion(i)} style={{ background: 'none', border: 'none', color: '#ef4444', cursor: 'pointer', fontSize: '18px', padding: '4px' }}>
                    &times;
                  </button>
                )}
              </div>
            ))}
          </div>
        </div>

        {/* Severity Bands */}
        <div style={{ backgroundColor: '#1e293b', border: '1px solid #4b5563', borderRadius: '8px', padding: '16px', marginBottom: '20px' }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '12px' }}>
            <h3 style={{ color: '#6CA6C1', margin: 0, fontSize: '16px' }}>Severity Bands</h3>
            <button onClick={addBand} style={{ padding: '6px 14px', backgroundColor: '#6CA6C1', color: '#fff', border: 'none', borderRadius: '6px', fontSize: '13px', cursor: 'pointer' }}>
              + Add Band
            </button>
          </div>
          <div style={{ display: 'grid', gap: '10px' }}>
            {bands.map((band, i) => (
              <div key={i} style={{ display: 'flex', alignItems: 'center', gap: '8px', backgroundColor: '#28527A', padding: '10px', borderRadius: '6px', borderLeft: `4px solid ${band.color}` }}>
                <input value={band.label} onChange={e => updateBand(i, 'label', e.target.value)} placeholder="Label" style={{ ...inputStyle, flex: 1, backgroundColor: '#374151' }} />
                <input type="number" value={band.min} onChange={e => updateBand(i, 'min', e.target.value)} style={{ ...inputStyle, width: '60px', textAlign: 'center' }} min={0} />
                <span style={{ color: '#9ca3af' }}>to</span>
                <input type="number" value={band.max} onChange={e => updateBand(i, 'max', e.target.value)} style={{ ...inputStyle, width: '60px', textAlign: 'center' }} min={0} />
                <input type="color" value={band.color} onChange={e => updateBand(i, 'color', e.target.value)} style={{ width: '32px', height: '32px', border: 'none', cursor: 'pointer', borderRadius: '4px' }} />
                {bands.length > 1 && (
                  <button onClick={() => removeBand(i)} style={{ background: 'none', border: 'none', color: '#ef4444', cursor: 'pointer', fontSize: '18px' }}>
                    &times;
                  </button>
                )}
              </div>
            ))}
          </div>
        </div>

        {/* Citation */}
        <div style={{ marginBottom: '24px' }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '6px' }}>
            <label style={{ ...labelStyle, marginBottom: 0 }}>Citation (optional)</label>
            <VoiceInputButton onResult={text => setCitation(prev => prev + ' ' + text)} />
          </div>
          <textarea value={citation} onChange={e => setCitation(e.target.value)} placeholder="Academic citation or source reference..." rows={2} style={{ ...inputStyle, resize: 'vertical' }} />
        </div>

        {/* Save / Cancel */}
        <div style={{ display: 'flex', gap: '12px' }}>
          <button onClick={handleSave} style={{ padding: '12px 24px', backgroundColor: '#D4A017', color: '#fff', border: 'none', borderRadius: '6px', fontSize: '14px', fontWeight: '600', cursor: 'pointer' }}>
            {editing ? 'Update Measure' : 'Create Measure'}
          </button>
          <button onClick={() => { setShowBuilder(false); resetForm(); }} style={{ padding: '12px 24px', backgroundColor: '#374151', color: '#e5e7eb', border: '1px solid #4b5563', borderRadius: '6px', fontSize: '14px', cursor: 'pointer' }}>
            Cancel
          </button>
        </div>
      </div>
    );
  }

  // ─── Measure List ───
  return (
    <div>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '24px' }}>
        <div>
          <h1 style={{ fontSize: '24px', fontWeight: '600', color: '#F7E9A0', margin: '0 0 8px 0' }}>Custom Measures</h1>
          <p style={{ fontSize: '14px', color: '#e5e7eb', margin: 0 }}>
            Create screening tools and outcome measures for your practice. Custom measures appear alongside GAD-7 and PHQ-9 in the Measures tab.
          </p>
        </div>
        <button onClick={() => openBuilder()} style={{ padding: '10px 20px', backgroundColor: '#6CA6C1', color: '#fff', border: 'none', borderRadius: '6px', fontSize: '14px', fontWeight: '600', cursor: 'pointer', whiteSpace: 'nowrap' }}>
          + New Measure
        </button>
      </div>

      {measures.length === 0 ? (
        <div style={{ textAlign: 'center', padding: '48px 20px', backgroundColor: '#1e293b', borderRadius: '8px', border: '1px dashed #4b5563' }}>
          <div style={{ fontSize: '48px', marginBottom: '12px' }}>📋</div>
          <h3 style={{ color: '#f9fafb', margin: '0 0 8px 0' }}>No Custom Measures Yet</h3>
          <p style={{ color: '#9ca3af', margin: '0 0 16px 0' }}>
            Create your first custom screening measure to use alongside GAD-7 and PHQ-9.
          </p>
          <button onClick={() => openBuilder()} style={{ padding: '10px 20px', backgroundColor: '#D4A017', color: '#fff', border: 'none', borderRadius: '6px', fontSize: '14px', fontWeight: '600', cursor: 'pointer' }}>
            Create Your First Measure
          </button>
        </div>
      ) : (
        <div style={{ display: 'grid', gap: '12px' }}>
          {measures.map(measure => (
            <div key={measure.id} style={{ backgroundColor: '#1e293b', border: '1px solid #4b5563', borderRadius: '8px', padding: '16px', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              <div>
                <h3 style={{ color: '#f9fafb', margin: '0 0 4px 0', fontSize: '16px' }}>{measure.title}</h3>
                <p style={{ color: '#9ca3af', margin: 0, fontSize: '13px' }}>
                  {measure.questions.length} questions &middot; Max score: {measure.maxScore} &middot; {measure.bands.length} severity bands
                </p>
                {measure.description && (
                  <p style={{ color: '#d1d5db', margin: '4px 0 0 0', fontSize: '13px' }}>{measure.description}</p>
                )}
                <div style={{ display: 'flex', gap: '6px', marginTop: '8px' }}>
                  {measure.bands.map((band, i) => (
                    <span key={i} style={{ padding: '2px 8px', borderRadius: '10px', backgroundColor: band.color + '22', color: band.color, fontSize: '11px', fontWeight: '500' }}>
                      {band.label} ({band.min}-{band.max})
                    </span>
                  ))}
                </div>
              </div>
              <div style={{ display: 'flex', gap: '8px' }}>
                <button onClick={() => openBuilder(measure)} style={{ padding: '8px 16px', backgroundColor: '#374151', color: '#e5e7eb', border: '1px solid #4b5563', borderRadius: '6px', fontSize: '13px', cursor: 'pointer' }}>
                  Edit
                </button>
                <button onClick={() => handleDelete(measure)} style={{ padding: '8px 16px', backgroundColor: '#374151', color: '#ef4444', border: '1px solid #4b5563', borderRadius: '6px', fontSize: '13px', cursor: 'pointer' }}>
                  Delete
                </button>
              </div>
            </div>
          ))}
        </div>
      )}
      {ConfirmDialog}
    </div>
  );
};

export default CustomMeasureBuilder;
